import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Trash2, UserMinus, ShieldCheck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import { inputClass, labelClass, sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"

interface Member {
    id: string
    userId: string
    role: string
    name: string
    email: string
}

interface Invitation {
    id: string
    inviteeEmail: string
    role: string
    expiresAt: string
}

interface InviteForm {
    email: string
    role: "owner" | "manager"
}

export const TeamSection = ({ arealId }: { arealId: string }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const membersKey = ["admin", "members", arealId]
    const invitesKey = ["admin", "invitations", arealId]

    const { data: members = [] } = useQuery({
        queryKey: membersKey,
        queryFn: () => api.get<Member[]>(`/areals/${arealId}/members`),
    })

    const { data: invitations = [] } = useQuery({
        queryKey: invitesKey,
        queryFn: () => api.get<Invitation[]>(`/areals/${arealId}/invitations`),
    })

    const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm<InviteForm>({
        defaultValues: { role: "manager" },
    })

    const inviteMutation = useMutation({
        mutationFn: (data: InviteForm) => api.post(`/areals/${arealId}/invitations`, data),
        onSuccess: () => {
            toast.success(t("admin.settings.teamInviteToastSent"))
            queryClient.invalidateQueries({ queryKey: invitesKey })
            reset({ email: "", role: "manager" })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.teamInviteToastFailed"))
        },
    })

    const revokeMutation = useMutation({
        mutationFn: (inviteId: string) => api.delete(`/areals/${arealId}/invitations/${inviteId}`),
        onSuccess: () => {
            toast.success(t("admin.settings.teamRevokeToastDone"))
            queryClient.invalidateQueries({ queryKey: invitesKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.teamRevokeToastFailed"))
        },
    })

    const removeMutation = useMutation({
        mutationFn: (userId: string) => api.delete(`/areals/${arealId}/members/${userId}`),
        onSuccess: () => {
            toast.success(t("admin.settings.teamRemoveToastDone"))
            queryClient.invalidateQueries({ queryKey: membersKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.teamRemoveToastFailed"))
        },
    })

    const roleMutation = useMutation({
        mutationFn: ({ userId, role }: { userId: string; role: string }) =>
            api.patch(`/areals/${arealId}/members/${userId}`, { role }),
        onSuccess: () => {
            toast.success(t("admin.settings.teamRoleToastDone"))
            queryClient.invalidateQueries({ queryKey: membersKey })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.teamRoleToastFailed"))
        },
    })

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionTeam")}</h2>
            <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                    {t("admin.settings.teamMembers")}
                </p>
                {members.length === 0 ? (
                    <p className="text-sm text-slate-400 font-bold">{t("admin.settings.teamNoMembers")}</p>
                ) : (
                    <ul className="space-y-2">
                        {members.map((m) => (
                            <li
                                key={m.id}
                                className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-200"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-black text-sm text-slate-900 truncate">{m.name}</p>
                                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                                </div>
                                <select
                                    value={m.role}
                                    onChange={(e) => roleMutation.mutate({ userId: m.userId, role: e.target.value })}
                                    className="text-xs font-bold border border-slate-200 rounded-xl px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand"
                                >
                                    <option value="owner">{t("admin.settings.teamRoleOwner")}</option>
                                    <option value="manager">{t("admin.settings.teamRoleManager")}</option>
                                </select>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm(t("admin.settings.teamConfirmRemove"))) {
                                            removeMutation.mutate(m.userId)
                                        }
                                    }}
                                    className="p-1.5 rounded-xl text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                >
                                    <UserMinus className="size-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div>
                <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                    {t("admin.settings.teamPendingInvitations")}
                </p>
                {invitations.length === 0 ? (
                    <p className="text-sm text-slate-400 font-bold">{t("admin.settings.teamNoInvitations")}</p>
                ) : (
                    <ul className="space-y-2">
                        {invitations.map((inv) => (
                            <li
                                key={inv.id}
                                className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-200"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-black text-sm text-slate-900 truncate">{inv.inviteeEmail}</p>
                                    <p className="text-xs text-slate-500">
                                        {inv.role === "owner"
                                            ? t("admin.settings.teamRoleOwner")
                                            : t("admin.settings.teamRoleManager")}
                                        {" — "}
                                        {t("admin.settings.teamExpiresLabel")}{" "}
                                        {new Date(inv.expiresAt).toLocaleDateString()}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm(t("admin.settings.teamConfirmRevoke"))) {
                                            revokeMutation.mutate(inv.id)
                                        }
                                    }}
                                    className="p-1.5 rounded-xl text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                >
                                    <Trash2 className="size-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div>
                <p className={labelClass}>{t("admin.settings.teamInvite")}</p>
                <form onSubmit={handleSubmit((d) => inviteMutation.mutate(d))} className="flex gap-2 flex-wrap">
                    <input
                        type="email"
                        placeholder={t("admin.settings.teamInviteEmail")}
                        {...register("email", { required: true })}
                        className={cn(inputClass, "flex-1 min-w-48")}
                    />
                    <select {...register("role")} className={cn(inputClass, "w-36")}>
                        <option value="manager">{t("admin.settings.teamInviteRoleManager")}</option>
                        <option value="owner">{t("admin.settings.teamInviteRoleOwner")}</option>
                    </select>
                    <button
                        type="submit"
                        disabled={isSubmitting || inviteMutation.isPending}
                        className={primaryBtnClass}
                    >
                        <ShieldCheck className="size-4" />
                        {inviteMutation.isPending
                            ? t("admin.settings.teamInviteSending")
                            : t("admin.settings.teamInviteSend")}
                    </button>
                </form>
            </div>
        </div>
    )
}
